/**
 * Official store badges → the mobile app ("Kalpa by Sarvalok").
 *
 * Self-hosted official badge art (public/badges/): Google Play PNG + App Store
 * SVG. Links point at the LIVE store pages — they 404 until each store's
 * public release, so this component ships to the VPS at launch moment (Play
 * first; the Apple badge can be commented out until the iOS release if the
 * two launches are staggered).
 *
 * The Play PNG is 646x250, while its visible badge occupies 564x168. Scale
 * the full image enough for that visible artwork to match the Apple badge,
 * and align its visible bounds rather than the transparent image canvas.
 */
const PLAY_URL = 'https://play.google.com/store/apps/details?id=com.sarvalok.mobile';
const APP_STORE_URL = 'https://apps.apple.com/app/id6788932903';

/**
 * Release switches — flip each to true ONLY when that store's PUBLIC listing
 * is live (the links 404 before that). Lets the Android-first launch ship the
 * Play badge alone, with the Apple badge following at iOS launch — one
 * constant, no markup edits.
 */
const SHOW_PLAY_BADGE = true;
const SHOW_APPLE_BADGE = true;

const PLAY_ASSET_HEIGHT = 250;
const PLAY_VISIBLE_BADGE_HEIGHT = 168;
const PLAY_VISIBLE_BADGE_WIDTH = 564;
const PLAY_ASSET_INSET = 41;
const APP_STORE_ASPECT_RATIO = 119.66407 / 40;

type StoreBadgesProps = {
  className?: string;
  /** Optical badge height in px (Apple badge height; Play auto-compensates). */
  height?: number;
  /** Per-usage overrides; default to the release switches above. */
  showPlay?: boolean;
  showApple?: boolean;
};

function StoreBadges({
  className = '',
  height = 40,
  showPlay = SHOW_PLAY_BADGE,
  showApple = SHOW_APPLE_BADGE,
}: StoreBadgesProps) {
  if (!showPlay && !showApple) return null;
  const playScale = height / PLAY_VISIBLE_BADGE_HEIGHT;
  return (
    <div
      className={`flex flex-wrap items-center ${className}`}
      style={{ gap: height / 2 }}
    >
      {showApple && (
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Download on the App Store"
          className="shrink-0"
        >
          <img
            src="/badges/app-store-badge.svg"
            alt="Download on the App Store"
            style={{ height, width: height * APP_STORE_ASPECT_RATIO }}
            className="block w-auto"
            loading="lazy"
          />
        </a>
      )}
      {showPlay && (
        <a
          href={PLAY_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Get it on Google Play"
          className="relative shrink-0"
          style={{ height, width: PLAY_VISIBLE_BADGE_WIDTH * playScale }}
        >
          <img
            src="/badges/google-play-badge.png"
            alt="Get it on Google Play"
            style={{
              height: PLAY_ASSET_HEIGHT * playScale,
              top: -PLAY_ASSET_INSET * playScale,
              left: -PLAY_ASSET_INSET * playScale,
            }}
            className="absolute block w-auto max-w-none"
            loading="lazy"
          />
        </a>
      )}
    </div>
  );
}

export default StoreBadges;
